import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { addDoc, getDoc, setDoc, onSnapshot, serverTimestamp, updateDoc, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { heartbeatDoc, heartbeatLogsColl, usersColl } from '../db';
import { queueNotification } from '../hooks/usePushNotification';
import { UserProfile, Screen } from '../App';
import { recordActivity } from '../utils/activityFeed';

interface Props {
  user: UserProfile;
  navigate: (s: Screen) => void;
  showToast: (msg: string) => void;
  backTo?: Screen;
}

interface Heartbeat {
  fromName: string;
  fromUid: string;
  timestamp: any;
  unread: boolean;
}

interface PartnerTarget {
  uid: string;
  coupleId: string;
  displayName: string;
}

interface HeartbeatLog {
  action: 'sent' | 'cooldown' | 'partner_missing' | 'failed';
  fromName: string;
  toName: string;
  notificationTitle?: string;
  notificationBody?: string;
  createdAt: any;
}

const VAPID_KEY = process.env.REACT_APP_VAPID_KEY || '';
const HEARTBEAT_COOLDOWN_MS = 5 * 60 * 1000;

const notificationTitle = (name: string) => `${name} seni düşündü`;
const notificationBody = 'Aklındasın.';

function normalizeIdentity(value = '') {
  return value
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/ç/g, 'c')
    .replace(/ğ/g, 'g')
    .replace(/ö/g, 'o')
    .replace(/ş/g, 's')
    .replace(/ü/g, 'u');
}

function timestampToMillis(value: any) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  return 0;
}

function formatRemaining(ms: number) {
  const totalSeconds = Math.max(1, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (!minutes) return `${seconds} sn`;
  return `${minutes} dk ${seconds.toString().padStart(2, '0')} sn`;
}

function HeartParticles() {
  const particles = useMemo(() =>
    Array.from({ length: 14 }, (_, i) => ({
      id: i,
      angle: (i / 14) * 360 + (Math.random() - 0.5) * 22,
      distance: 85 + Math.random() * 115,
      delay: Math.random() * 0.28,
      size: 14 + Math.random() * 14,
    })), [] // eslint-disable-line
  );

  return (
    <>
      {particles.map(p => {
        const rad = (p.angle * Math.PI) / 180;
        return (
          <motion.span
            key={p.id}
            initial={{ x: 0, y: 0, opacity: 1, scale: 0.3 }}
            animate={{
              x: Math.cos(rad) * p.distance,
              y: Math.sin(rad) * p.distance,
              opacity: 0,
              scale: 1.1,
            }}
            transition={{ duration: 0.85, delay: p.delay, ease: 'easeOut' }}
            style={{ position: 'absolute', fontSize: p.size, pointerEvents: 'none', userSelect: 'none' }}
          >
            💗
          </motion.span>
        );
      })}
    </>
  );
}

export default function ImHere({ user, navigate, showToast, backTo = 'bond' }: Props) {
  const [partnerTarget, setPartnerTarget] = useState<PartnerTarget | null>(null);
  const [partnerStatus, setPartnerStatus] = useState('');
  const [partnerBeat, setPartnerBeat] = useState<Heartbeat | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [particleKey, setParticleKey] = useState(0);
  const [incomingBanner, setIncomingBanner] = useState<{ name: string } | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  const [lastSentLog, setLastSentLog] = useState<HeartbeatLog | null>(null);
  const [, setCooldownTick] = useState(0);

  const partnerName = user.displayName === 'Serhat' ? 'Ayça' : 'Serhat';
  const cooldownRemaining = cooldownUntil ? Math.max(0, cooldownUntil - Date.now()) : 0;

  const logHeartbeat = useCallback(async (
    action: 'sent' | 'cooldown' | 'partner_missing' | 'failed',
    detail: Record<string, any> = {}
  ) => {
    try {
      await addDoc(heartbeatLogsColl(user.coupleId), {
        action,
        fromUid: user.uid,
        fromName: user.displayName,
        toUid: partnerTarget?.uid || null,
        toName: partnerTarget?.displayName || partnerName,
        targetCoupleId: partnerTarget?.coupleId || null,
        notificationTitle: notificationTitle(user.displayName),
        notificationBody,
        ...detail,
        createdAt: serverTimestamp(),
      });
    } catch (err) {
      console.warn('[ImHere] Log write failed:', err);
    }
  }, [partnerName, partnerTarget, user.coupleId, user.displayName, user.uid]);

  useEffect(() => {
    if (!cooldownUntil) return undefined;
    if (cooldownUntil <= Date.now()) {
      setCooldownUntil(null);
      return undefined;
    }

    const timer = window.setInterval(() => {
      setCooldownTick(t => t + 1);
      if (cooldownUntil <= Date.now()) setCooldownUntil(null);
    }, 1000);

    return () => window.clearInterval(timer);
  }, [cooldownUntil]);

  useEffect(() => {
    let alive = true;

    const init = async () => {
      setPartnerStatus(`${partnerName} hesabı aranıyor...`);
      const snap = await getDocs(usersColl());

      const directPartner = user.partnerUid
        ? snap.docs.find(d => d.id === user.partnerUid)
        : undefined;

      const sameCouplePartner = snap.docs.find(d =>
        d.id !== user.uid && d.data().coupleId === user.coupleId
      );

      const targetName = normalizeIdentity(partnerName);
      const identityPartner = snap.docs.find(d => {
        if (d.id === user.uid) return false;
        const data = d.data();
        return (
          normalizeIdentity(data.displayName).includes(targetName) ||
          normalizeIdentity(data.email).includes(targetName)
        );
      });

      const partner = directPartner || sameCouplePartner || identityPartner;
      if (!alive) return;

      if (!partner) {
        setPartnerTarget(null);
        setPartnerStatus(`${partnerName} hesabı uygulamada bulunamadı. ${partnerName} bir kez giriş yapınca bu buton aktif olur.`);
        return;
      }

      const data = partner.data();
      setPartnerTarget({
        uid: partner.id,
        coupleId: data.coupleId || user.coupleId,
        displayName: data.displayName || partnerName,
      });

      const targetCoupleId = data.coupleId || user.coupleId;
      const lastSignal = await getDoc(heartbeatDoc(targetCoupleId, partner.id));
      if (lastSignal.exists()) {
        const signal = lastSignal.data();
        if (signal.fromUid === user.uid) {
          const nextAllowedAt = timestampToMillis(signal.timestamp) + HEARTBEAT_COOLDOWN_MS;
          if (nextAllowedAt > Date.now()) setCooldownUntil(nextAllowedAt);
        }
      }

      setPartnerStatus('');
    };

    init().catch((err) => {
      console.warn('[ImHere] Partner lookup failed:', err);
      if (!alive) return;
      setPartnerTarget(null);
      setPartnerStatus(`${partnerName} hesabı kontrol edilemedi. Birazdan tekrar deneyin.`);
    });

    return () => {
      alive = false;
    };
  }, [user.uid, user.coupleId, user.partnerUid, partnerName]);

  useEffect(() => {
    return onSnapshot(heartbeatDoc(user.coupleId, user.uid), snap => {
      if (!snap.exists()) return;
      const data = snap.data() as Heartbeat;
      setPartnerBeat(data);
      if (data.unread) {
        setIncomingBanner({ name: data.fromName });
        updateDoc(heartbeatDoc(user.coupleId, user.uid), { unread: false });
        setTimeout(() => setIncomingBanner(null), 3000);
      }
    });
  }, [user.coupleId, user.uid]);

  useEffect(() => {
    const logsQuery = query(
      heartbeatLogsColl(user.coupleId),
      orderBy('createdAt', 'desc'),
      limit(8)
    );

    return onSnapshot(logsQuery, snap => {
      const latestSent = snap.docs
        .map(d => d.data() as HeartbeatLog)
        .find(log => log.action === 'sent');
      setLastSentLog(latestSent || null);
    });
  }, [user.coupleId]);

  const send = useCallback(async () => {
    if (sending) return;

    if (!partnerTarget) {
      const message = `${partnerName} hesabı henüz bağlı değil. ${partnerName} bir kez giriş yapınca gönderebilirsin.`;
      setPartnerStatus(message);
      showToast(message);
      await logHeartbeat('partner_missing', { reason: 'partner target not found' });
      return;
    }

    setSending(true);
    try {
      const now = Date.now();
      if (cooldownUntil && cooldownUntil > now) {
        const remaining = formatRemaining(cooldownUntil - now);
        setPartnerStatus(`Bir sonraki bildirimi ${remaining} sonra gönderebilirsin.`);
        showToast(`Biraz bekleyelim: ${remaining}`);
        await logHeartbeat('cooldown', { cooldownUntil: new Date(cooldownUntil), remainingMs: cooldownUntil - now });
        return;
      }

      const lastSignal = await getDoc(heartbeatDoc(partnerTarget.coupleId, partnerTarget.uid));
      if (lastSignal.exists()) {
        const data = lastSignal.data();
        if (data.fromUid === user.uid) {
          const nextAllowedAt = timestampToMillis(data.timestamp) + HEARTBEAT_COOLDOWN_MS;
          if (nextAllowedAt > Date.now()) {
            const remaining = formatRemaining(nextAllowedAt - Date.now());
            setCooldownUntil(nextAllowedAt);
            setPartnerStatus(`Bir sonraki bildirimi ${remaining} sonra gönderebilirsin.`);
            showToast(`Biraz bekleyelim: ${remaining}`);
            await logHeartbeat('cooldown', { cooldownUntil: new Date(nextAllowedAt), remainingMs: nextAllowedAt - Date.now() });
            return;
          }
        }
      }

      await setDoc(heartbeatDoc(partnerTarget.coupleId, partnerTarget.uid), {
        fromName: user.displayName,
        fromUid: user.uid,
        timestamp: serverTimestamp(),
        unread: true,
      });
      await queueNotification(
        partnerTarget.coupleId,
        partnerTarget.uid,
        notificationTitle(user.displayName),
        notificationBody,
        'heartbeat'
      );
      const nextCooldown = Date.now() + HEARTBEAT_COOLDOWN_MS;
      setCooldownUntil(nextCooldown);
      await logHeartbeat('sent', {
        cooldownUntil: new Date(nextCooldown),
        notificationQueued: true,
      });
      recordActivity(user, {
        type: 'heartbeat',
        title: 'Bir işaret gönderildi',
        body: `${user.displayName}, ${partnerTarget.displayName || partnerName} için "aklındasın" dedi.`,
        sourceId: user.uid,
      }).catch(() => {});
      console.info('[ImHere] Heartbeat notification queued:', {
        from: user.displayName,
        to: partnerTarget.displayName,
        title: notificationTitle(user.displayName),
        body: notificationBody,
        cooldownUntil: new Date(nextCooldown).toISOString(),
      });
      setParticleKey(k => k + 1);
      setSent(true);
      setPartnerStatus('');
      showToast('Mesajın gönderildi');
      setTimeout(() => setSent(false), 3200);
    } catch (err) {
      console.error('[ImHere] Heartbeat send failed:', err);
      setPartnerStatus('Gönderilemedi. İnternet bağlantısını veya Firebase izinlerini kontrol etmek gerekiyor.');
      showToast('Gönderilemedi');
      await logHeartbeat('failed', { error: err instanceof Error ? err.message : String(err) });
    } finally {
      setSending(false);
    }
  }, [cooldownUntil, logHeartbeat, partnerTarget, partnerName, sending, user, showToast]);

  const formatTime = (ts: any) => {
    if (!ts?.toDate) return '';
    const d = ts.toDate();
    const diff = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diff < 1) return 'az önce';
    if (diff < 60) return `${diff} dakika önce`;
    if (diff < 1440) return `${Math.floor(diff / 60)} saat önce`;
    return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  };

  return (
    <div>
      {/* Incoming heartbeat banner */}
      <AnimatePresence>
        {incomingBanner && (
          <motion.div
            initial={{ y: -90, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -90, opacity: 0 }}
            transition={{ type: 'spring', damping: 22, stiffness: 280 }}
            style={styles.banner}
          >
            <motion.span
              animate={{ scale: [1, 1.35, 1, 1.2, 1] }}
              transition={{ duration: 0.7, repeat: 3 }}
              style={{ fontSize: 34 }}
            >
              💗
            </motion.span>
            <div>
              <p style={styles.bannerTitle}>{incomingBanner.name} seni düşündü</p>
              <p style={styles.bannerSub}>Aklındasın.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(backTo)}>←</button>
        <h1 style={styles.title}>Bir işaret gönder</h1>
      </div>

      <div style={styles.main}>
        {/* Send section */}
        <div style={styles.sendSection}>
          <p style={styles.sendLabel}>{partnerName}'a küçük bir işaret gönder</p>

          {/* Button + particles container */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 200, height: 200 }}>
            {/* Particle bursts — remounts on each send */}
            {particleKey > 0 && (
              <AnimatePresence>
                <HeartParticles key={particleKey} />
              </AnimatePresence>
            )}

            {/* Pulse rings */}
            <AnimatePresence>
              {sent && (
                <>
                  <motion.div
                    key="ring1"
                    initial={{ scale: 1, opacity: 0.55 }}
                    animate={{ scale: 2.6, opacity: 0 }}
                    exit={{}}
                    transition={{ duration: 1.3, repeat: 2, ease: 'easeOut' }}
                    style={styles.pulseRing}
                  />
                  <motion.div
                    key="ring2"
                    initial={{ scale: 1, opacity: 0.35 }}
                    animate={{ scale: 2.6, opacity: 0 }}
                    exit={{}}
                    transition={{ duration: 1.3, repeat: 2, ease: 'easeOut', delay: 0.35 }}
                    style={styles.pulseRing}
                  />
                </>
              )}
            </AnimatePresence>

            <motion.button
              onClick={send}
              disabled={sending || sent || cooldownRemaining > 0}
              whileTap={{ scale: 0.9 }}
              animate={sent ? { scale: [1, 1.15, 1.07] } : {}}
              transition={{ duration: 0.4 }}
              style={{
                ...styles.heartBtn,
                background: sent
                  ? 'linear-gradient(135deg, rgba(233,30,140,0.45), rgba(156,39,176,0.45))'
                  : 'linear-gradient(135deg, rgba(233,30,140,0.14), rgba(156,39,176,0.14))',
                borderColor: sent ? 'rgba(233,30,140,0.85)' : 'rgba(233,30,140,0.38)',
                boxShadow: sent
                  ? '0 0 60px rgba(233,30,140,0.6), 0 0 120px rgba(233,30,140,0.2)'
                  : '0 0 40px rgba(233,30,140,0.22)',
              }}
            >
              <motion.span
                animate={sent ? { scale: [1, 1.4, 1.15] } : {}}
                transition={{ duration: 0.45 }}
                style={{ fontSize: 62 }}
              >
                {sent ? '💕' : '🤍'}
              </motion.span>
            </motion.button>
          </div>

          <AnimatePresence mode="wait">
            <motion.p
              key={sent ? 'sent' : sending ? 'sending' : 'idle'}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2 }}
              style={styles.sendHint}
            >
              {sent
                ? 'Mesaj gönderildi'
                : sending
                  ? 'Gönderiliyor...'
                  : cooldownRemaining > 0
                    ? `${formatRemaining(cooldownRemaining)} sonra tekrar`
                    : '5 dakikada bir gönderilebilir'}
            </motion.p>
          </AnimatePresence>

          {partnerStatus && <p style={styles.statusNote}>{partnerStatus}</p>}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 220, damping: 24 }}
          style={styles.lastLogCard}
        >
          <p style={styles.lastLogLabel}>Son gönderim</p>
          {lastSentLog ? (
            <>
              <p style={styles.lastLogTitle}>{lastSentLog.fromName} → {lastSentLog.toName}</p>
              <p style={styles.lastLogBody}>
                {lastSentLog.notificationTitle || notificationTitle(lastSentLog.fromName)}
                {lastSentLog.notificationBody ? ` · ${lastSentLog.notificationBody}` : ''}
              </p>
              <p style={styles.lastLogTime}>{formatTime(lastSentLog.createdAt)}</p>
            </>
          ) : (
            <p style={styles.lastLogEmpty}>Henüz gönderim yok.</p>
          )}
        </motion.div>

        {/* Partner's last signal */}
        <AnimatePresence mode="wait">
          {partnerBeat ? (
            <motion.div
              key="beat"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 220, damping: 20 }}
              style={styles.partnerCard}
            >
              <motion.span
                animate={{ scale: [1, 1.22, 1] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
                style={{ fontSize: 34 }}
              >
                💌
              </motion.span>
              <div>
                <p style={styles.partnerTitle}>{partnerBeat.fromName} işaret bıraktı</p>
                <p style={styles.partnerTime}>{formatTime(partnerBeat.timestamp)}</p>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="wait"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={styles.waitCard}
            >
              <motion.p
                animate={{ opacity: [0.4, 1, 0.4] }}
                transition={{ duration: 2.6, repeat: Infinity }}
                style={{ fontSize: 34 }}
              >
                💫
              </motion.p>
              <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 14 }}>{partnerName}'dan henüz sinyal yok</p>
            </motion.div>
          )}
        </AnimatePresence>

        <p style={styles.note}>
          {VAPID_KEY ? 'Uygulama kapalıyken de bildirim alırsın 💕' : 'Uygulama açıkken anlık bildirim alırsın 💕'}
        </p>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  title: { fontFamily: "'Playfair Display',serif", fontSize: 22 },
  banner: {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 400,
    background: 'linear-gradient(135deg, rgba(233,30,140,0.96), rgba(120,20,140,0.96))',
    padding: '18px 22px', display: 'flex', alignItems: 'center', gap: 16,
    backdropFilter: 'blur(20px)',
    boxShadow: '0 6px 32px rgba(233,30,140,0.45)',
  },
  bannerTitle: { fontSize: 16, fontWeight: 700, color: 'white' },
  bannerSub: { fontSize: 13, color: 'rgba(255,255,255,0.72)', marginTop: 3 },
  main: { padding: '8px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 28 },
  sendSection: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, paddingTop: 20 },
  sendLabel: { color: 'rgba(255,255,255,0.58)', fontSize: 14, textAlign: 'center' },
  pulseRing: {
    position: 'absolute',
    width: 168, height: 168, borderRadius: '50%',
    border: '2px solid rgba(233,30,140,0.6)',
    pointerEvents: 'none',
  },
  heartBtn: {
    width: 168, height: 168, borderRadius: '50%',
    border: '2px solid',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    position: 'relative', flexShrink: 0,
  },
  sendHint: { color: 'rgba(255,255,255,0.48)', fontSize: 14, textAlign: 'center' },
  statusNote: {
    maxWidth: 310,
    color: 'rgba(255,255,255,0.52)',
    fontSize: 13,
    lineHeight: 1.45,
    textAlign: 'center',
  },
  lastLogCard: {
    width: '100%',
    borderRadius: 22,
    padding: '18px 20px',
    background: 'rgba(255,255,255,0.045)',
    border: '1px solid rgba(255,255,255,0.08)',
    backdropFilter: 'blur(18px)',
  },
  lastLogLabel: {
    color: 'rgba(255,255,255,0.42)',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  lastLogTitle: {
    color: 'white',
    fontSize: 18,
    fontWeight: 800,
    marginBottom: 6,
  },
  lastLogBody: {
    color: 'rgba(255,255,255,0.68)',
    fontSize: 14,
    lineHeight: 1.4,
  },
  lastLogTime: {
    color: 'rgba(255,255,255,0.36)',
    fontSize: 12,
    marginTop: 10,
  },
  lastLogEmpty: {
    color: 'rgba(255,255,255,0.44)',
    fontSize: 14,
  },
  partnerCard: {
    background: 'linear-gradient(135deg, rgba(233,30,140,0.11), rgba(156,39,176,0.11))',
    border: '1px solid rgba(233,30,140,0.24)',
    borderRadius: 20, padding: '18px 22px',
    display: 'flex', alignItems: 'center', gap: 16, width: '100%',
  },
  partnerTitle: { fontSize: 15, fontWeight: 600, marginBottom: 4 },
  partnerTime: { fontSize: 13, color: 'rgba(255,255,255,0.45)' },
  waitCard: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
    padding: 28, background: 'rgba(255,255,255,0.04)', borderRadius: 20,
    border: '1px solid rgba(255,255,255,0.07)', width: '100%',
  },
  note: { color: 'rgba(255,255,255,0.22)', fontSize: 12, textAlign: 'center' },
};
